// QUERY FILTERS
import { Nullable } from "../../../common/types/nullable";
import { TalentStatus } from "../../talents/types/common";
import { MessageAttachment } from "../attachments/types";
import { ConversationType } from "./commands";
import { Initiator, ParticipantType, Sender } from "./common";

export interface ListConversationMessagesFilters {
  conversationId: string;
  limit?: number;
}

export interface ListConversationMessagesFiltersThunk
  extends ListConversationMessagesFilters {
  isInitial?: true;
}

export interface ListConversationsFilters {
  status: ConversationStatus;
  assigned?: true; // legacy index filter; do not send together with inbox
  reset?: true;
  nextToken?: string;
  limit?: number;
  inbox?: "TEAM" | "MY";
}

export interface SearchConversationsFilters {
  search: string;
  nextToken?: string;
  limit?: number;
}

export interface SearchConversationsFiltersThunk
  extends SearchConversationsFilters {
  isInitial?: true;
}

export interface ListConversationsFiltersThunk
  extends ListConversationsFilters {
  reset?: true; // TODO: is this needed anymore?
  isInitial?: true;
  inbox?: "TEAM" | "MY";
}

export enum ConversationStatus {
  DRAFT = "DRAFT",
  OPEN = "OPEN",
  IN_PROGRESS = "IN_PROGRESS",
  CLOSED = "CLOSED",
}

export enum MessageTemplate {
  SHIFT_OPEN = "SHIFT_OPEN",
  SHIFT_FULFILLED = "SHIFT_FULFILLED",
  SHIFT_BUNDLE_OPEN = "SHIFT_BUNDLE_OPEN",
  SHIFT_BUNDLE_FULFILLED = "SHIFT_BUNDLE_FULFILLED",
}

// QUERY RESPONSES
export interface Conversation {
  id: string;
  type: ConversationType; // TODO: move to common
  status: ConversationStatus;
  channel: string;
  assigneeId: Nullable<string>;
  initiator: Initiator;
  lastMessage: {
    id: string;
    conversationId: string;
    text: string;
    senderId: string;
    sentAt: string; // "2025-05-04T15:54:01.152Z";
    sender: Sender;
  } | null;
  createdAt?: string;
  topic?: string | null;
  topicId?: string | null;
  teamIds?: string[];
  participants: Participant[];
  unreadMessageCount: number;
  lastReadMessageId: Nullable<string>;
  messageTemplate?: MessageTemplate;
}

export interface ListConversations {
  conversations: Conversation[];
  nextToken?: string;
}

export interface TopicTranslation {
  languageCode: string;
  name: string;
  description: string;
  displayOrder: number;
}

export type TopicRoutingDestinationType =
  | "PARTICIPANTS"
  | "SUPPORT_TEAMS"
  | "OWN_HR"
  | "HR_TEAMS";

export interface TopicRoutingPolicy {
  conversationType: ConversationType;
  destination: { type: TopicRoutingDestinationType };
}

export interface ConversationTopic {
  id: string;
  translations: TopicTranslation[];
  routingPolicies: TopicRoutingPolicy[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListTopics {
  items: ConversationTopic[];
  nextToken?: string;
}

export interface MessagingTeam {
  id: string;
  name: string;
  type?: "SUPPORT" | "HR";
  memberIds: string[];
}

export interface ListTeams {
  items: MessagingTeam[];
  nextToken?: string;
}

export interface InboxSummary {
  myOpen: number;
  teamOpen: number;
  inProgress: {
    total: number;
    unread: number;
  };
  hasUnread: boolean;
}

export enum MessageType {
  TEXT = "TEXT",
}

// TODO: rename
export interface ChatMessage extends Record<string, any> {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  messageType: MessageType.TEXT;
  sentAt: string;
  attachments?: MessageAttachment[];
  // Participants whose read cursor includes this message
  readBy?: string[];
  // The receipt icon waits until the send command succeeds
  pending?: boolean;
}

export interface Participant {
  id: string;
  firstName: string;
  lastName: string;
  type: ParticipantType;
  city?: string; // Talent
  customerCompanyName?: string; // Customer

  // these field are enriched from projection service in "fetchConversations" action
  talentStatus?: TalentStatus;
  customerWorkLocationNames?: string[];
}

export interface ListConversationMessagesResponse {
  conversationId: string;
  participants: Participant[];
  messages: ChatMessage[];
  nextToken?: string;
}
